'use client';

import DateRangeCalendar from '@/app/ui/survey/date-range-calendar';
import SurveyTopBar from '@/app/ui/survey/survey-top-bar';
import { confirmModalAtom } from '@/atom/modal';
import { surveyAtom } from '@/atom/survey';
import dayjs from 'dayjs';
import { useAtom, useSetAtom } from 'jotai';
import isDate from 'lodash/isDate';
import { useRouter } from 'next/navigation';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import 'dayjs/locale/ko';
import TimePrimary2020 from '@svg/survey/when/time_primary_20_20.svg';
import TimeSelectBottomModal from '@/app/ui/survey/time-select-bottom-modal';
import { HolidayCGType } from '@/types/graphql/root';
import client from '@/utils/apollo-client';
import { GetHolidaysQuery } from '@/utils/gql';
import { historyStackAtom } from '@/atom/historyStack';
import useBlockBackward from '@/app/hooks/useBlockBackward';

const DEFAULT_START_TIME = '11:00';
const DEFAULT_END_TIME = '20:00';
const TIMES = [
  { label: '12:00  AM', value: '00:00' },
  { label: '1:00  AM', value: '01:00' },
  { label: '2:00  AM', value: '02:00' },
  { label: '3:00  AM', value: '03:00' },
  { label: '4:00  AM', value: '04:00' },
  { label: '5:00  AM', value: '05:00' },
  { label: '6:00  AM', value: '06:00' },
  { label: '7:00  AM', value: '07:00' },
  { label: '8:00  AM', value: '08:00' },
  { label: '9:00  AM', value: '09:00' },
  { label: '10:00  AM', value: '10:00' },
  { label: '11:00  AM', value: '11:00' },
  { label: '12:00  PM', value: '12:00' },
  { label: '1:00  PM', value: '13:00' },
  { label: '2:00  PM', value: '14:00' },
  { label: '3:00  PM', value: '15:00' },
  { label: '4:00  PM', value: '16:00' },
  { label: '5:00  PM', value: '17:00' },
  { label: '6:00  PM', value: '18:00' },
  { label: '7:00  PM', value: '19:00' },
  { label: '8:00  PM', value: '20:00' },
  { label: '9:00  PM', value: '21:00' },
  { label: '10:00  PM', value: '22:00' },
  { label: '11:00  PM', value: '23:00' },
];

function BlockBackwardSuspenseChild() {
  // 뒤로가기 버튼 블락처리할 페이지에서 custom hooks 사용
  useBlockBackward({
    title: '정말로 나가시겠어요?',
    message: '응답내용이 저장되지 않아요.',
  });

  return null;
}

export default function Page() {
  const router = useRouter();
  const setHistoryStack = useSetAtom(historyStackAtom);

  const setConfirmModal = useSetAtom(confirmModalAtom);
  const [survey, setSurvey] = useAtom(surveyAtom);

  const [from, setFrom] = useState<Date | undefined>(
    survey.fromDateTime ? dayjs(survey.fromDateTime).startOf('day').toDate() : undefined,
  );
  const [to, setTo] = useState<Date | undefined>(
    survey.toDateTime ? dayjs(survey.toDateTime).startOf('day').toDate() : undefined,
  );
  const [calendarFocus, setCalendarFocus] = useState<'startDate' | 'endDate'>('startDate');
  const [calendarMonth, setCalendarMonth] = useState<Date | null>(null);
  const [minimumDate, setMinimumDate] = useState<Date | null>(null);

  const [holidays, setHolidays] = useState<HolidayCGType[]>([]);

  const [startTime, setStartTime] = useState<string>(
    survey.fromDateTime ? dayjs(survey.fromDateTime).format('HH:mm') : DEFAULT_START_TIME,
  );
  const [endTime, setEndTime] = useState<string>(
    survey.toDateTime ? dayjs(survey.toDateTime).format('HH:mm') : DEFAULT_END_TIME,
  );
  const [timeType, setTimeType] = useState<'start' | 'end' | undefined>(undefined);

  const fromReady = useMemo(() => isDate(from), [from]);
  const toReady = useMemo(() => isDate(to), [to]);
  const timeVisible = useMemo(() => {
    return isDate(from) && isDate(to) && dayjs(from).format('YYYY-MM-DD') !== dayjs(to).format('YYYY-MM-DD');
  }, [from, to]);

  const startTimeLabel = useMemo(() => {
    return TIMES.find((time) => time.value === startTime)?.value;
  }, [startTime]);
  const endTimeLabel = useMemo(() => {
    return TIMES.find((time) => time.value === endTime)?.value;
  }, [endTime]);

  const onSelectTime = useCallback((time: string, timeType: 'start' | 'end') => {
    if (timeType === 'start') {
      setStartTime(time);
    } else {
      setEndTime(time);
    }
  }, []);

  useEffect(() => {
    setCalendarMonth(survey.fromDateTime ? dayjs(survey.fromDateTime).startOf('month').toDate() : new Date());
    setMinimumDate(new Date());
  }, []);

  useEffect(() => {
    if (!toReady) return;
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  }, [toReady]);

  const year = useMemo(() => {
    return dayjs(calendarMonth).format('YYYY');
  }, [calendarMonth]);

  useEffect(() => {
    if (!calendarMonth) return;
    client
      .query({
        query: GetHolidaysQuery,
        fetchPolicy: 'cache-first',
        variables: {
          start: `${year}-01-01`,
          end: `${year}-12-31`,
        },
      })
      .then(({ data }) => {
        setHolidays(data.holidays);
      });
  }, [year]);

  return (
    <div className='w-full pb-120pxr loaded-wrap'>
      <Suspense>
        <BlockBackwardSuspenseChild />
      </Suspense>
      <SurveyTopBar
        title='날짜 선택'
        onClickBack={() => {
          router.back();
        }}
      />
      <h5 className='flex flex-row items-center py-24pxr px-20pxr gap-12pxr'>
        <p className='text-custom-black heading-18-b'>언제 떠나시나요?</p>
        <span className='text-point-400 body-14-r'>*최대 5박 6일 선택 가능</span>
      </h5>

      <div className='relative w-full'>
        <DateRangeCalendar
          startDate={from ?? null}
          endDate={to ?? null}
          onStartDateChange={(date) => {
            if (date) {
              setTo(undefined);
              setFrom(date);
            }
          }}
          onEndDateChange={(date) => {
            if (date) {
              const fromDayjs = dayjs(from);
              const toDayjs = dayjs(date);
              const diff = toDayjs.diff(fromDayjs, 'day');
              if (diff < 0) {
                setFrom(date);
                setTo(fromDayjs.toDate());
              } else {
                setTo(date);
              }
            }
          }}
          focus={calendarFocus}
          onFocusChange={(newFocus) => setCalendarFocus(newFocus || 'startDate')}
          maximumLength={5}
          minimumDate={minimumDate}
          month={calendarMonth}
          onMonthChange={(date) => {
            if (!date) return;
            setCalendarMonth(date);
          }}
          holidays={holidays}
        />
      </div>

      {(fromReady || toReady) && (
        <div className='flex flex-col border-t border-gray-200 mt-20pxr mx-20pxr py-24pxr gap-12pxr'>
          {fromReady && (
            <div className='flex flex-col items-start w-full'>
              <span className='text-gray-600 sub-heading-14-m'>일정 시작</span>
              <div className='flex flex-row items-center justify-between w-full h-49pxr'>
                <span className='text-custom-black sub-heading-16-m'>
                  {from && dayjs(from).locale('ko').format('YYYY.MM.DD (ddd)')}
                </span>
                {timeVisible && (
                  <button
                    className='flex flex-row items-center justify-center border border-gray-200 px-12pxr py-14pxr gap-6pxr rounded-6pxr'
                    onClick={() => {
                      setTimeType('start');
                    }}
                  >
                    <TimePrimary2020 className='flex-none' />
                    <p className='text-gray-800 sub-heading-16-m'>{startTimeLabel}</p>
                  </button>
                )}
              </div>
            </div>
          )}
          {toReady && (
            <div className='flex flex-col items-start w-full'>
              <span className='text-gray-600 sub-heading-14-m'>일정 종료</span>
              <div className='flex flex-row items-center justify-between w-full h-49pxr'>
                <span className='text-custom-black sub-heading-16-m'>
                  {to && dayjs(to).locale('ko').format('YYYY.MM.DD (ddd)')}
                </span>
                {timeVisible && (
                  <button
                    className='flex flex-row items-center justify-center border border-gray-200 px-12pxr py-14pxr gap-6pxr rounded-6pxr'
                    onClick={() => {
                      setTimeType('end');
                    }}
                  >
                    <TimePrimary2020 className='flex-none' />
                    <p className='text-gray-800 sub-heading-16-m'>{endTimeLabel}</p>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <footer
        className='fixed bottom-0 z-[2] w-full bg-white h-100pxr rounded-t-16pxr px-20pxr pt-16pxr pb-34pxr max-w-480pxr'
        style={{ boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)' }}
      >
        <button
          className={`w-full h-full rounded-8pxr ${fromReady && toReady ? 'bg-custom-black' : 'bg-gray-300'}`}
          disabled={!(fromReady && toReady)}
          onClick={() => {
            if (!from || !to) return;

            setSurvey({
              ...survey,
              fromDateTime: `${dayjs(from).format('YYYY-MM-DD')} ${startTime}`,
              toDateTime: `${dayjs(to).format('YYYY-MM-DD')} ${endTime}`,
            });
            router.push('/survey/who');
          }}
        >
          <p className='text-white'>다음</p>
        </button>
      </footer>

      <TimeSelectBottomModal
        currentSelectedTime={timeType === 'start' ? startTime : endTime}
        timeType={timeType}
        setTimeType={setTimeType}
        onSelectTime={onSelectTime}
      />
    </div>
  );
}
